import { coins, getNetworkLabel } from "../constants/constants";

// ─── Types ──────────────────────────────────────────────────────────────────

export type FeeType = "FLAT" | "PERCENTAGE";

export type PartnerNetwork = {
  networkName: string;
  networkCode: string;
  minimumDeposit: number;
  depositFee: number;
  minimumWithdrawal: number;
  withdrawalFee: number;
  maximumDecimalPlaces: number;
  receiveFeeType: FeeType;
  withdrawalFeeType: FeeType;
};

export type PartnerCurrency = {
  currencyName: string;
  currencyType: "CRYPTO" | "STABLE";
  networks: PartnerNetwork[];
};

export type PartnerWalletDetails = Record<string, PartnerCurrency>;

export type NetworkOption = {
  name: string;
  abbr: string;
};

export type WithdrawalConfig = {
  minimumWithdrawal: number;
  withdrawalFee: number;
  withdrawalFeeType: FeeType;
  maximumDecimalPlaces: number;
  source: "api" | "fallback";
};

// ─── Supported Assets Whitelist ─────────────────────────────────────────────

type SupportedAsset = {
  currency: string;
  apiAliases?: string[];
  networks: string[];
};

const SUPPORTED_ASSETS: SupportedAsset[] = [
  { currency: "USDT", networks: ["BSC", "ETH", "TRX", "SOL"] },
  { currency: "USDC", networks: ["BSC", "ETH", "SOL"] },
  { currency: "BTC", networks: ["BTC"] },
  { currency: "ETH", networks: ["ETH", "BSC"] },
  { currency: "SOL", networks: ["SOL"] },
  { currency: "TRX", networks: ["TRX"] },
  { currency: "DOGE", networks: ["DOGE"] },
  { currency: "SHIB", networks: ["ETH", "BSC"] },
  { currency: "BNB", networks: ["ETH", "BSC"] },
  { currency: "POL", apiAliases: ["MATIC"], networks: ["ETH", "MATIC", "BSC"] },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function findSupportedAsset(currency?: string | null): SupportedAsset | undefined {
  if (!currency) return undefined;
  const normalized = currency.trim().toUpperCase();
  return SUPPORTED_ASSETS.find(
    (asset) =>
      asset.currency === normalized ||
      asset.apiAliases?.includes(normalized) === true,
  );
}

/**
 * Every code this environment's backend might use for `currency`, ours first.
 */
function assetCodeCandidates(currency: string): string[] {
  const asset = findSupportedAsset(currency);
  return asset ? [asset.currency, ...(asset.apiAliases ?? [])] : [currency];
}

/**
 * The ticker to send to the API for `currency`.
 *
 * Obiex renamed MATIC to POL, but only production has caught up — staging still
 * serves MATIC and rejects POL outright. `apiCodes` is the list the backend
 * itself reported (GET /api/rates), so we speak back whichever spelling it
 * knows rather than guessing from the build. Falls back to our own code when
 * rates haven't loaded, which is what every call site used to send anyway.
 */
export function resolveApiAssetCode(
  currency: string | null | undefined,
  apiCodes: string[],
): string {
  if (!currency) return "";
  const normalized = currency.trim().toUpperCase();
  if (!apiCodes.length) return normalized;

  const match = assetCodeCandidates(normalized).find((code) =>
    apiCodes.includes(code),
  );

  return match ?? normalized;
}

/** Whether the backend offers `currency` under any name we recognise. */
export function isAssetOfferedByApi(
  currency: string | null | undefined,
  apiCodes: string[],
): boolean {
  if (!currency) return false;
  // Nothing to filter against until rates load — don't empty the picker.
  if (!apiCodes.length) return true;

  return assetCodeCandidates(currency.trim().toUpperCase()).some((code) =>
    apiCodes.includes(code),
  );
}

const POLYGON_CODES = new Set(["MATIC", "POL", "POLYGON"]);

export function matchesNetwork(a: string, b: string): boolean {
  const left = a.trim().toUpperCase();
  const right = b.trim().toUpperCase();
  if (left === right) return true;
  return POLYGON_CODES.has(left) && POLYGON_CODES.has(right);
}

export function matchesCurrency(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const left = findSupportedAsset(a);
  const right = findSupportedAsset(b);
  if (left && right) return left.currency === right.currency;
  return a.trim().toUpperCase() === b.trim().toUpperCase();
}

function findCurrency(
  details: PartnerWalletDetails,
  currency: string,
): PartnerCurrency | undefined {
  const asset = findSupportedAsset(currency);
  if (!asset) return details[currency];
  const key = [asset.currency, ...(asset.apiAliases ?? [])].find(
    (code) => details[code] !== undefined,
  );
  return key ? details[key] : undefined;
}

function getFallbackConfig(currency: string, network: string) {
  const asset = findSupportedAsset(currency);
  const coinAbbr = asset?.currency ?? currency;
  const coin = coins.find((c: any) => c.abbr === coinAbbr);
  if (!coin) return undefined;

  const canonical =
    asset?.networks.find((n) => matchesNetwork(n, network)) ?? network;
  const net = coin.networks.find((n: any) => matchesNetwork(n.name, canonical));
  if (!net) return undefined;
  return { minimumWithdrawal: net.minimumWithdrawal, withdrawalFee: net.withdrawalFee };
}

/** Codes we declare for `asset` that this payload is actually serving. */
function partnerServedNetworks(
  details: PartnerWalletDetails,
  asset: SupportedAsset,
): string[] {
  const available = findCurrency(details, asset.currency);
  if (!available) return [];

  return asset.networks.filter((code) =>
    available.networks.some((n) => matchesNetwork(n.networkCode, code)),
  );
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Networks to offer for a coin: what the partner currently serves, narrowed to
 * what we support.
 *
 * A payload that loaded is authoritative — if it doesn't carry the coin, we
 * offer nothing. Only a payload we never got falls back to the local snapshot.
 */
export function getSupportedNetworks(
  details: PartnerWalletDetails | undefined,
  currency?: string | null,
): NetworkOption[] {
  const asset = findSupportedAsset(currency);
  if (!asset) return [];

  const codes = details
    ? partnerServedNetworks(details, asset)
    : asset.networks.filter(
        (code) => getFallbackConfig(asset.currency, code) !== undefined,
      );

  return codes.map((code) => ({
    name: getNetworkLabel(code),
    abbr: code,
  }));
}

/**
 * Withdrawal limits for a pair. Live partner data wins; the hardcoded table is
 * only used when we couldn't reach them at all. If the payload loaded and this
 * pair isn't in it, returns undefined — callers must treat that as unavailable,
 * never as a zero fee.
 */
export function getWithdrawalConfig(
  details: PartnerWalletDetails | undefined,
  currency?: string | null,
  network?: string | null,
): WithdrawalConfig | undefined {
  const asset = findSupportedAsset(currency);
  if (!asset || !network) return undefined;

  const code = network.trim().toUpperCase();
  const canonicalCode = asset.networks.find((n) => matchesNetwork(n, code));
  if (!canonicalCode) return undefined;

  if (details) {
    const partnerNetwork = findCurrency(
      details,
      asset.currency,
    )?.networks.find((n) => matchesNetwork(n.networkCode, canonicalCode));

    if (!partnerNetwork) return undefined;

    return {
      minimumWithdrawal: partnerNetwork.minimumWithdrawal,
      withdrawalFee: partnerNetwork.withdrawalFee,
      withdrawalFeeType: partnerNetwork.withdrawalFeeType,
      maximumDecimalPlaces: partnerNetwork.maximumDecimalPlaces,
      source: "api",
    };
  }

  const local = getFallbackConfig(asset.currency, canonicalCode);
  if (!local) return undefined;

  return {
    minimumWithdrawal: local.minimumWithdrawal,
    withdrawalFee: local.withdrawalFee,
    withdrawalFeeType: "FLAT",
    maximumDecimalPlaces: 8,
    source: "fallback",
  };
}

export function computeWithdrawalFee(
  amount: number,
  config?: WithdrawalConfig,
): number {
  if (!config || !Number.isFinite(amount) || amount <= 0) return 0;
  if (config.withdrawalFeeType === "PERCENTAGE") {
    return (amount * config.withdrawalFee) / 100;
  }
  return config.withdrawalFee;
}

export function computeAmountReceived(
  amount: number,
  config?: WithdrawalConfig,
): number {
  return Math.max(0, amount - computeWithdrawalFee(amount, config));
}

export function getEffectiveMinimum(config?: WithdrawalConfig): number {
  if (!config) return 0;
  if (config.withdrawalFeeType === "PERCENTAGE") {
    return config.minimumWithdrawal;
  }
  return Math.max(config.minimumWithdrawal, config.withdrawalFee);
}

export function isBelowMinimum(
  amount: number | string,
  config?: WithdrawalConfig,
): boolean {
  const value = Number(amount);
  if (!Number.isFinite(value)) return true;
  return value < getEffectiveMinimum(config);
}
